import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Navigation from "@/components/Navigation";
import { 
  Mic, 
  MicOff, 
  Loader2, 
  Send, 
  Volume2, 
  Globe, 
  MessageSquare,
  Sparkles,
  ArrowRight,
  HelpCircle,
  Copy,
  Languages
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Language configurations with proper voice settings
const LANGUAGES = {
  en: {
    code: 'en',
    name: 'English',
    flag: '🇺🇸',
    voiceLang: 'en-US',
    greeting: 'Hello! How can I help you today?',
    placeholder: 'Type your message or tap the mic...',
    listening: '🎤 Listening... Speak now!',
    processing: '⏳ Processing...',
    tapToSpeak: 'Tap the microphone to speak',
    examples: [
      { text: "Send $100 to John", icon: "💸" },
      { text: "What's my balance?", icon: "💰" },
      { text: "How much did I spend this week?", icon: "📊" },
      { text: "What are your fees?", icon: "💵" }
    ],
    quickCommands: [
      "Check balance",
      "Send money",
      "View history",
      "Help"
    ]
  },
  fr: {
    code: 'fr',
    name: 'Français',
    flag: '🇫🇷',
    voiceLang: 'fr-FR',
    greeting: 'Bonjour! Comment puis-je vous aider aujourd\'hui?',
    placeholder: 'Tapez votre message ou appuyez sur le micro...',
    listening: '🎤 Écoute... Parlez maintenant!',
    processing: '⏳ Traitement...',
    tapToSpeak: 'Appuyez sur le microphone pour parler',
    examples: [
      { text: "Envoyer 100€ à Jean", icon: "💸" },
      { text: "Quel est mon solde?", icon: "💰" },
      { text: "Combien ai-je dépensé cette semaine?", icon: "📊" },
      { text: "Quels sont vos frais?", icon: "💵" }
    ],
    quickCommands: [
      "Vérifier solde",
      "Envoyer argent",
      "Voir historique",
      "Aide"
    ]
  },
  ar: {
    code: 'ar',
    name: 'العربية',
    flag: '🇸🇦',
    voiceLang: 'ar-SA',
    greeting: 'مرحباً! كيف يمكنني مساعدتك اليوم؟',
    placeholder: 'اكتب رسالتك أو اضغط على الميكروفون...',
    listening: '🎤 أستمع... تحدث الآن!',
    processing: '⏳ جاري المعالجة...',
    tapToSpeak: 'اضغط على الميكروفون للتحدث',
    examples: [
      { text: "أرسل 100 دولار لأحمد", icon: "💸" },
      { text: "ما هو رصيدي؟", icon: "💰" },
      { text: "كم أنفقت هذا الأسبوع؟", icon: "📊" },
      { text: "ما هي رسومكم؟", icon: "💵" }
    ],
    quickCommands: [
      "تحقق من الرصيد",
      "إرسال مال",
      "عرض السجل",
      "مساعدة"
    ]
  },
  es: {
    code: 'es',
    name: 'Español',
    flag: '🇪🇸',
    voiceLang: 'es-ES',
    greeting: '¡Hola! ¿Cómo puedo ayudarte hoy?',
    placeholder: 'Escribe tu mensaje o toca el micrófono...',
    listening: '🎤 Escuchando... ¡Habla ahora!',
    processing: '⏳ Procesando...',
    tapToSpeak: 'Toca el micrófono para hablar',
    examples: [
      { text: "Enviar $100 a Juan", icon: "💸" },
      { text: "¿Cuál es mi saldo?", icon: "💰" },
      { text: "¿Cuánto gasté esta semana?", icon: "📊" },
      { text: "¿Cuáles son sus tarifas?", icon: "💵" }
    ],
    quickCommands: [
      "Ver saldo",
      "Enviar dinero",
      "Ver historial",
      "Ayuda"
    ]
  },
  pt: {
    code: 'pt',
    name: 'Português',
    flag: '🇧🇷',
    voiceLang: 'pt-BR',
    greeting: 'Olá! Como posso ajudá-lo hoje?',
    placeholder: 'Digite sua mensagem ou toque no microfone...',
    listening: '🎤 Ouvindo... Fale agora!',
    processing: '⏳ Processando...',
    tapToSpeak: 'Toque no microfone para falar',
    examples: [
      { text: "Enviar $100 para João", icon: "💸" },
      { text: "Qual é meu saldo?", icon: "💰" },
      { text: "Quanto gastei esta semana?", icon: "📊" },
      { text: "Quais são suas taxas?", icon: "💵" }
    ],
    quickCommands: [
      "Ver saldo",
      "Enviar dinheiro",
      "Ver histórico",
      "Ajuda"
    ]
  },
  de: {
    code: 'de',
    name: 'Deutsch',
    flag: '🇩🇪',
    voiceLang: 'de-DE',
    greeting: 'Hallo! Wie kann ich Ihnen heute helfen?',
    placeholder: 'Nachricht eingeben oder Mikrofon antippen...',
    listening: '🎤 Hören... Sprechen Sie jetzt!',
    processing: '⏳ Verarbeitung...',
    tapToSpeak: 'Tippen Sie auf das Mikrofon zum Sprechen',
    examples: [
      { text: "Sende 100€ an Hans", icon: "💸" },
      { text: "Wie ist mein Kontostand?", icon: "💰" },
      { text: "Wie viel habe ich diese Woche ausgegeben?", icon: "📊" },
      { text: "Was sind Ihre Gebühren?", icon: "💵" }
    ],
    quickCommands: [
      "Kontostand prüfen",
      "Geld senden",
      "Verlauf anzeigen",
      "Hilfe"
    ]
  },
  it: {
    code: 'it',
    name: 'Italiano',
    flag: '🇮🇹',
    voiceLang: 'it-IT',
    greeting: 'Ciao! Come posso aiutarti oggi?',
    placeholder: 'Digita il tuo messaggio o tocca il microfono...',
    listening: '🎤 Ascolto... Parla ora!',
    processing: '⏳ Elaborazione...',
    tapToSpeak: 'Tocca il microfono per parlare',
    examples: [
      { text: "Invia $100 a Marco", icon: "💸" },
      { text: "Qual è il mio saldo?", icon: "💰" },
      { text: "Quanto ho speso questa settimana?", icon: "📊" },
      { text: "Quali sono le vostre tariffe?", icon: "💵" }
    ],
    quickCommands: [
      "Controlla saldo",
      "Invia denaro",
      "Vedi cronologia",
      "Aiuto"
    ]
  },
  zh: {
    code: 'zh',
    name: '中文',
    flag: '🇨🇳',
    voiceLang: 'zh-CN',
    greeting: '您好！今天我能帮您什么？',
    placeholder: '输入消息或点击麦克风...',
    listening: '🎤 正在听... 请说话！',
    processing: '⏳ 处理中...',
    tapToSpeak: '点击麦克风说话',
    examples: [
      { text: "汇款100美元给王伟", icon: "💸" },
      { text: "我的余额是多少？", icon: "💰" },
      { text: "这周花了多少钱？", icon: "📊" },
      { text: "你们的费用是多少？", icon: "💵" }
    ],
    quickCommands: [
      "查看余额",
      "汇款",
      "查看历史",
      "帮助"
    ]
  },
  hi: {
    code: 'hi',
    name: 'हिन्दी',
    flag: '🇮🇳',
    voiceLang: 'hi-IN',
    greeting: 'नमस्ते! आज मैं आपकी कैसे मदद कर सकता हूं?',
    placeholder: 'अपना संदेश टाइप करें या माइक टैप करें...',
    listening: '🎤 सुन रहा हूं... अभी बोलें!',
    processing: '⏳ प्रोसेसिंग...',
    tapToSpeak: 'बोलने के लिए माइक्रोफोन टैप करें',
    examples: [
      { text: "राज को $100 भेजें", icon: "💸" },
      { text: "मेरा बैलेंस क्या है?", icon: "💰" },
      { text: "इस हफ्ते कितना खर्च किया?", icon: "📊" },
      { text: "आपकी फीस क्या है?", icon: "💵" }
    ],
    quickCommands: [
      "बैलेंस देखें",
      "पैसे भेजें",
      "इतिहास देखें",
      "मदद"
    ]
  },
  ja: {
    code: 'ja',
    name: '日本語',
    flag: '🇯🇵',
    voiceLang: 'ja-JP',
    greeting: 'こんにちは！今日はどのようにお手伝いしましょうか？',
    placeholder: 'メッセージを入力するかマイクをタップ...',
    listening: '🎤 聞いています... 今すぐ話してください！',
    processing: '⏳ 処理中...',
    tapToSpeak: 'マイクをタップして話す',
    examples: [
      { text: "田中さんに100ドル送金", icon: "💸" },
      { text: "残高はいくらですか？", icon: "💰" },
      { text: "今週いくら使いましたか？", icon: "📊" },
      { text: "手数料はいくらですか？", icon: "💵" }
    ],
    quickCommands: [
      "残高確認",
      "送金する",
      "履歴を見る",
      "ヘルプ"
    ]
  },
  ko: {
    code: 'ko',
    name: '한국어',
    flag: '🇰🇷',
    voiceLang: 'ko-KR',
    greeting: '안녕하세요! 오늘 어떻게 도와드릴까요?',
    placeholder: '메시지를 입력하거나 마이크를 탭하세요...',
    listening: '🎤 듣고 있습니다... 지금 말씀하세요!',
    processing: '⏳ 처리 중...',
    tapToSpeak: '마이크를 탭하여 말하기',
    examples: [
      { text: "김철수에게 $100 보내기", icon: "💸" },
      { text: "내 잔액은 얼마인가요?", icon: "💰" },
      { text: "이번 주에 얼마나 썼나요?", icon: "📊" },
      { text: "수수료는 얼마인가요?", icon: "💵" }
    ],
    quickCommands: [
      "잔액 확인",
      "송금하기",
      "내역 보기",
      "도움말"
    ]
  },
  tr: {
    code: 'tr',
    name: 'Türkçe',
    flag: '🇹🇷',
    voiceLang: 'tr-TR',
    greeting: 'Merhaba! Bugün size nasıl yardımcı olabilirim?',
    placeholder: 'Mesajınızı yazın veya mikrofona dokunun...',
    listening: '🎤 Dinliyorum... Şimdi konuşun!',
    processing: '⏳ İşleniyor...',
    tapToSpeak: 'Konuşmak için mikrofona dokunun',
    examples: [
      { text: "Ahmet'e $100 gönder", icon: "💸" },
      { text: "Bakiyem ne kadar?", icon: "💰" },
      { text: "Bu hafta ne kadar harcadım?", icon: "📊" },
      { text: "Ücretleriniz nedir?", icon: "💵" }
    ],
    quickCommands: [
      "Bakiye kontrol",
      "Para gönder",
      "Geçmişi gör",
      "Yardım"
    ]
  }
};

export default function VoiceAssistantPage({ user, onLogout }) {
  const navigate = useNavigate();
  
  // Check if language was previously selected (persisted in sessionStorage)
  const [showLanguageModal, setShowLanguageModal] = useState(() => {
    return !sessionStorage.getItem('voiceLanguageSelected');
  });
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    return sessionStorage.getItem('voiceLanguage') || 'en';
  });
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [conversation, setConversation] = useState([]);
  const [status, setStatus] = useState('ready');
  
  // Transfer state for voice-initiated transfers
  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [createdTransfer, setCreatedTransfer] = useState(null);
  const [isCreatingTransfer, setIsCreatingTransfer] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const conversationEndRef = useRef(null);
  const synthRef = useRef(null);
  const createdTransferRef = useRef(null);

  // Handle language selection from modal
  const handleLanguageSelect = (langCode) => {
    setSelectedLanguage(langCode);
    sessionStorage.setItem('voiceLanguage', langCode);
    sessionStorage.setItem('voiceLanguageSelected', 'true');
    setShowLanguageModal(false);
    // Reset conversation with new language greeting
    setConversation([{
      role: 'assistant',
      text: LANGUAGES[langCode].greeting,
      timestamp: new Date()
    }]);
  };

  // Debug: Log when component mounts/updates
  useEffect(() => {
    console.log('VoiceAssistantPage mounted/updated. createdTransfer:', createdTransfer);
  }, [createdTransfer]);

  // Debug: Log render
  console.log('VoiceAssistantPage render. createdTransfer:', createdTransfer ? 'SET' : 'NULL');

  const lang = LANGUAGES[selectedLanguage];

  // Auto-scroll to latest message
  useEffect(() => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  // Initialize with greeting - only on first mount
  useEffect(() => {
    if (conversation.length === 0 && !createdTransfer) {
      setConversation([{
        role: 'assistant',
        text: lang.greeting,
        timestamp: new Date()
      }]);
    }
  }, []);

  // Update greeting when language changes
  useEffect(() => {
    if (conversation.length === 1 && conversation[0].role === 'assistant') {
      setConversation([{
        role: 'assistant',
        text: lang.greeting,
        timestamp: new Date()
      }]);
    }
  }, [selectedLanguage]);

  // Get the best voice for the selected language
  const getBestVoice = (langCode) => {
    if (!('speechSynthesis' in window)) return null;
    
    const voices = speechSynthesis.getVoices();
    const langPrefix = langCode.split('-')[0];
    
    // Try to find a high-quality voice for this language
    let voice = voices.find(v => v.lang === langCode && v.name.includes('Google'));
    if (!voice) voice = voices.find(v => v.lang === langCode && v.name.includes('Microsoft'));
    if (!voice) voice = voices.find(v => v.lang === langCode);
    if (!voice) voice = voices.find(v => v.lang.startsWith(langPrefix));
    
    return voice;
  };

  // Speak text with proper voice
  const speakText = (text) => {
    if (!('speechSynthesis' in window)) {
      console.warn('Speech synthesis not supported');
      return;
    }

    // Cancel any ongoing speech
    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang.voiceLang;
    
    // Get best voice for language
    const voice = getBestVoice(lang.voiceLang);
    if (voice) {
      utterance.voice = voice;
    }

    // Adjust settings for better quality
    utterance.rate = selectedLanguage === 'ar' ? 0.9 : 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    synthRef.current = utterance;
    speechSynthesis.speak(utterance);
  };

  // Speak claim code slowly (word by word)
  const speakClaimCode = (mnemonic) => {
    if (!mnemonic) return;
    
    const words = mnemonic.split(' ');
    const spokenText = words.map((word, i) => `${i + 1}. ${word}`).join('. ');
    
    const intro = selectedLanguage === 'es' ? 'Tu código de reclamo es:' :
                  selectedLanguage === 'fr' ? 'Votre code de réclamation est:' :
                  selectedLanguage === 'ar' ? 'رمز المطالبة الخاص بك هو:' :
                  'Your claim code is:';
    
    speakText(`${intro} ${spokenText}`);
  };

  // Create actual MTCN transfer
  const createVoiceTransfer = async () => {
    if (!pendingTransfer || !user) return;
    
    setIsCreatingTransfer(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/mtcn/create-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: pendingTransfer.amount,
          currency: pendingTransfer.currency || 'USD',
          sender_email: user.email,
          sender_name: user.full_name || 'RouteFlow User',
          recipient_last_name: pendingTransfer.recipient?.split(' ').pop() || pendingTransfer.recipient,
          message: `Voice transfer to ${pendingTransfer.recipient}`,
          send_email_backup: true
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Transfer created successfully:', data);
        
        // Extract recipient's last name for security answer
        const recipientLastName = pendingTransfer.recipient?.split(' ').pop() || pendingTransfer.recipient;
        
        const newCreatedTransfer = {
          ...data,
          recipient: pendingTransfer.recipient,
          recipient_last_name: recipientLastName,
          recipient_name: pendingTransfer.recipient,
          amount: pendingTransfer.amount,
          currency: pendingTransfer.currency || 'USD'
        };
        console.log('Setting createdTransfer state:', newCreatedTransfer);
        
        // Clear pending transfer first
        setPendingTransfer(null);
        
        const successMsg = getTransferSuccessMessage(pendingTransfer.amount, pendingTransfer.currency || 'USD', pendingTransfer.recipient);
        addMessage('assistant', successMsg);
        
        toast.success('Transfer created! Redirecting to details...');
        
        // Navigate to send-money page with the transfer details
        // Use setTimeout to avoid React concurrent rendering issues
        setTimeout(() => {
          navigate('/send-money', { 
            state: { 
              showTransferResult: true,
              transferData: newCreatedTransfer
            },
            replace: true
          });
        }, 100);
      } else {
        throw new Error(data.detail || 'Failed to create transfer');
      }
    } catch (error) {
      console.error('Transfer error:', error);
      const errorMsg = getTransferErrorMessage();
      addMessage('assistant', errorMsg);
      speakText(errorMsg);
      toast.error('Failed to create transfer');
    } finally {
      setIsCreatingTransfer(false);
    }
  };

  // Cancel pending transfer
  const cancelPendingTransfer = () => {
    setPendingTransfer(null);
    const cancelMsg = getCancelMessage();
    addMessage('assistant', cancelMsg);
    speakText(cancelMsg);
  };

  // Get success message in selected language - simplified, no "read code" prompt
  const getTransferSuccessMessage = (amount, currency, recipient) => {
    const msgs = {
      en: `Transfer created! Copy your claim code and proceed to payment.`,
      es: `¡Transferencia creada! Copia tu código y procede al pago.`,
      fr: `Transfert créé! Copiez votre code et procédez au paiement.`,
      ar: `تم إنشاء التحويل! انسخ الرمز وتابع للدفع.`
    };
    return msgs[selectedLanguage] || msgs.en;
  };

  // Get cancel message
  const getCancelMessage = () => {
    const msgs = {
      en: "Transfer cancelled. How else can I help you?",
      es: "Transferencia cancelada. ¿En qué más puedo ayudarte?",
      fr: "Transfert annulé. Comment puis-je vous aider autrement?",
      ar: "تم إلغاء التحويل. كيف يمكنني مساعدتك بشكل آخر؟"
    };
    return msgs[selectedLanguage] || msgs.en;
  };

  // Get transfer error message
  const getTransferErrorMessage = () => {
    const msgs = {
      en: "Sorry, there was an error creating your transfer. Please try again.",
      es: "Lo siento, hubo un error al crear tu transferencia. Por favor, inténtalo de nuevo.",
      fr: "Désolé, une erreur s'est produite lors de la création de votre transfert. Veuillez réessayer.",
      ar: "عذراً، حدث خطأ أثناء إنشاء التحويل. يرجى المحاولة مرة أخرى."
    };
    return msgs[selectedLanguage] || msgs.en;
  };

  // Get confirmation prompt message
  const getConfirmationPrompt = (amount, currency, recipient) => {
    const msgs = {
      en: `I'll send ${currency} ${amount} to ${recipient}. Say "confirm" to create the transfer, or "cancel" to stop.`,
      es: `Enviaré ${currency} ${amount} a ${recipient}. Di "confirmar" para crear la transferencia, o "cancelar" para detener.`,
      fr: `Je vais envoyer ${currency} ${amount} à ${recipient}. Dites "confirmer" pour créer le transfert, ou "annuler" pour arrêter.`,
      ar: `سأرسل ${currency} ${amount} إلى ${recipient}. قل "تأكيد" لإنشاء التحويل، أو "إلغاء" للتوقف.`
    };
    return msgs[selectedLanguage] || msgs.en;
  };

  // Stop speaking
  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        } 
      });
      
      // Check supported MIME types
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      }
      
      const mediaRecorder = new MediaRecorder(stream, { 
        mimeType,
        audioBitsPerSecond: 128000
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        
        if (audioChunksRef.current.length === 0) {
          console.error('No audio data captured');
          const errorMsg = getErrorMessage();
          addMessage('assistant', errorMsg);
          setIsProcessing(false);
          setStatus('ready');
          return;
        }
        
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        console.log('Audio captured:', audioBlob.size, 'bytes, type:', audioBlob.type);
        
        if (audioBlob.size < 1000) {
          console.error('Audio too short:', audioBlob.size, 'bytes');
          addMessage('assistant', selectedLanguage === 'es' ? 'No escuché nada. Por favor, habla más tiempo.' :
                                  selectedLanguage === 'fr' ? "Je n'ai rien entendu. Parlez plus longtemps." :
                                  selectedLanguage === 'ar' ? 'لم أسمع شيئاً. يرجى التحدث لفترة أطول.' :
                                  "I didn't hear anything. Please speak for longer.");
          setIsProcessing(false);
          setStatus('ready');
          return;
        }
        
        await processAudio(audioBlob);
      };

      // Start recording with timeslice to ensure data is captured frequently
      mediaRecorder.start(250); // Capture data every 250ms
      setIsRecording(true);
      setStatus('listening');
      stopSpeaking();
    } catch (error) {
      console.error('Microphone error:', error);
      toast.error('Please allow microphone access to use voice commands');
      setStatus('ready');
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setStatus('processing');
      setIsProcessing(true);
    }
  };

  // Process audio recording
  const processAudio = async (audioBlob) => {
    try {
      const formData = new FormData();
      // Use the actual blob type as filename extension
      const ext = audioBlob.type.includes('webm') ? 'webm' : 
                  audioBlob.type.includes('ogg') ? 'ogg' : 
                  audioBlob.type.includes('mp4') ? 'm4a' : 'webm';
      formData.append('audio', audioBlob, `recording.${ext}`);
      formData.append('user_id', user?.id || 'guest');
      formData.append('language', selectedLanguage);

      console.log('Sending audio for processing:', audioBlob.size, 'bytes');
      
      const response = await fetch(`${BACKEND_URL}/api/voice/command`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      console.log('Voice API response:', data);
      
      // Check if transcription failed
      if (data.error && data.error.includes('transcribe')) {
        console.error('Transcription failed:', data.error);
        const errorMsg = selectedLanguage === 'es' ? 'No pude entender el audio. Intenta hablar más claro o usa el teclado.' :
                         selectedLanguage === 'fr' ? "Je n'ai pas pu comprendre l'audio. Essayez de parler plus clairement ou utilisez le clavier." :
                         selectedLanguage === 'ar' ? 'لم أتمكن من فهم الصوت. حاول التحدث بوضوح أو استخدم لوحة المفاتيح.' :
                         "I couldn't understand the audio. Try speaking more clearly or use the keyboard.";
        addMessage('assistant', errorMsg);
        speakText(errorMsg);
        return;
      }
      
      // Add user message (transcribed text)
      const userText = data.transcribed_text || "Voice command";
      addMessage('user', userText);
      
      // Handle the response based on intent
      await handleVoiceResponse(data, userText);

    } catch (error) {
      console.error('Error:', error);
      const errorMsg = getErrorMessage();
      addMessage('assistant', errorMsg);
      speakText(errorMsg);
    } finally {
      setIsProcessing(false);
      setStatus('ready');
    }
  };

  // Process text command
  const processTextCommand = async (text) => {
    if (!text.trim()) return;

    setIsProcessing(true);
    setStatus('processing');
    stopSpeaking();

    // Add user message immediately
    addMessage('user', text);
    setTextInput('');

    // Check for local commands first (confirm, cancel, read code)
    const lowerText = text.toLowerCase();
    
    // Handle confirmation
    if (pendingTransfer && (
      lowerText.includes('confirm') || 
      lowerText.includes('okay') ||
      lowerText.includes('ok') ||
      lowerText.includes('yes') || 
      lowerText.includes('sure') ||
      lowerText.includes('go ahead') ||
      lowerText.includes('do it') ||
      lowerText.includes('confirmar') || 
      lowerText.includes('sí') ||
      lowerText.includes('oui') ||
      lowerText.includes('تأكيد') ||
      lowerText.includes('نعم')
    )) {
      console.log('Confirmation detected, creating transfer...');
      await createVoiceTransfer();
      setIsProcessing(false);
      setStatus('ready');
      return;
    }
    
    // Handle cancellation
    if (pendingTransfer && (
      lowerText.includes('cancel') || 
      lowerText.includes('no') || 
      lowerText.includes('cancelar') || 
      lowerText.includes('annuler') ||
      lowerText.includes('إلغاء')
    )) {
      cancelPendingTransfer();
      setIsProcessing(false);
      setStatus('ready');
      return;
    }
    
    // Handle "read my code" command
    if (createdTransfer && (
      lowerText.includes('read') && lowerText.includes('code') ||
      lowerText.includes('lee') && lowerText.includes('código') ||
      lowerText.includes('lis') && lowerText.includes('code') ||
      lowerText.includes('اقرأ') && lowerText.includes('كود')
    )) {
      speakClaimCode(createdTransfer.mnemonic);
      const readingMsg = selectedLanguage === 'es' ? 'Leyendo tu código de reclamo...' :
                        selectedLanguage === 'fr' ? 'Lecture de votre code...' :
                        selectedLanguage === 'ar' ? 'قراءة رمز المطالبة الخاص بك...' :
                        'Reading your claim code...';
      addMessage('assistant', readingMsg);
      setIsProcessing(false);
      setStatus('ready');
      return;
    }
    
    // Handle "proceed to payment" command after transfer creation
    if (createdTransfer && (
      (lowerText.includes('proceed') && lowerText.includes('payment')) ||
      (lowerText.includes('pay') && (lowerText.includes('now') || lowerText.includes('complete'))) ||
      lowerText.includes('complete payment') ||
      lowerText.includes('make payment') ||
      lowerText.includes('pagar') || // Spanish
      lowerText.includes('paiement') || // French
      lowerText.includes('الدفع') // Arabic
    )) {
      const paymentMsg = selectedLanguage === 'es' ? 'Abriendo página de pago...' :
                        selectedLanguage === 'fr' ? 'Ouverture de la page de paiement...' :
                        selectedLanguage === 'ar' ? 'فتح صفحة الدفع...' :
                        'Opening payment page...';
      addMessage('assistant', paymentMsg);
      
      // Navigate to payment immediately
      navigate('/send-money', { 
        state: { 
          prefill: {
            amount: createdTransfer.amount,
            currency: createdTransfer.currency,
            recipient: createdTransfer.recipient,
            walletAddress: createdTransfer.wallet_address,
            skipToPayment: true
          }
        }
      });
      setIsProcessing(false);
      setStatus('ready');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/voice/text-command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text,
          user_id: user?.id || 'guest',
          language: selectedLanguage
        })
      });

      const data = await response.json();
      
      // Handle the response based on intent
      await handleVoiceResponse(data, text);

    } catch (error) {
      console.error('Error:', error);
      const errorMsg = getErrorMessage();
      addMessage('assistant', errorMsg);
      speakText(errorMsg);
    } finally {
      setIsProcessing(false);
      setStatus('ready');
    }
  };

  // Handle voice response and determine action
  const handleVoiceResponse = async (data, userText) => {
    const lowerText = userText.toLowerCase();
    
    // Check for confirmation/cancellation commands
    if (pendingTransfer) {
      if (lowerText.includes('confirm') || lowerText.includes('yes') || 
          lowerText.includes('okay') || lowerText.includes('ok') ||
          lowerText.includes('sure') || lowerText.includes('go ahead') ||
          lowerText.includes('do it') ||
          lowerText.includes('confirmar') || lowerText.includes('sí') ||
          lowerText.includes('oui') ||
          lowerText.includes('تأكيد') || lowerText.includes('نعم')) {
        console.log('Confirmation detected in handleVoiceResponse');
        await createVoiceTransfer();
        return;
      }
      
      if (lowerText.includes('cancel') || lowerText.includes('no') ||
          lowerText.includes('stop') || lowerText.includes('nevermind') ||
          lowerText.includes('cancelar') || lowerText.includes('annuler') ||
          lowerText.includes('إلغاء') || lowerText.includes('لا')) {
        cancelPendingTransfer();
        return;
      }
    }
    
    // Check for "proceed to payment" command after transfer creation
    if (createdTransfer && (
      (lowerText.includes('proceed') && lowerText.includes('payment')) ||
      (lowerText.includes('pay') && (lowerText.includes('now') || lowerText.includes('complete'))) ||
      lowerText.includes('complete payment') ||
      lowerText.includes('make payment') ||
      lowerText.includes('pagar') || // Spanish
      lowerText.includes('paiement') || // French
      lowerText.includes('الدفع') // Arabic
    )) {
      const paymentMsg = selectedLanguage === 'es' ? 'Abriendo página de pago...' :
                        selectedLanguage === 'fr' ? 'Ouverture de la page de paiement...' :
                        selectedLanguage === 'ar' ? 'فتح صفحة الدفع...' :
                        'Opening payment page...';
      addMessage('assistant', paymentMsg);
      
      // Navigate to payment immediately
      navigate('/send-money', { 
        state: { 
          prefill: {
            amount: createdTransfer.amount,
            currency: createdTransfer.currency,
            recipient: createdTransfer.recipient,
            walletAddress: createdTransfer.wallet_address,
            skipToPayment: true
          }
        }
      });
      return;
    }
    
    // Check for "read code" command
    if (createdTransfer && (
      (lowerText.includes('read') && lowerText.includes('code')) ||
      (lowerText.includes('lee') && lowerText.includes('código')) ||
      (lowerText.includes('lis') && lowerText.includes('code')) ||
      lowerText.includes('اقرأ')
    )) {
      speakClaimCode(createdTransfer.mnemonic);
      const readingMsg = selectedLanguage === 'es' ? 'Leyendo tu código de reclamo...' :
                        selectedLanguage === 'fr' ? 'Lecture de votre code...' :
                        selectedLanguage === 'ar' ? 'قراءة رمز المطالبة الخاص بك...' :
                        'Reading your claim code...';
      addMessage('assistant', readingMsg);
      return;
    }
    
    // Handle send money intent - show preview and ask for confirmation
    if (data.intent === 'send_money' && data.amount && data.recipient) {
      console.log('Setting pending transfer:', data.amount, data.recipient);
      const transferDetails = {
        amount: parseFloat(data.amount),
        currency: data.currency || 'USD',
        recipient: data.recipient
      };
      
      setPendingTransfer(transferDetails);
      
      const confirmMsg = getConfirmationPrompt(data.amount, data.currency || 'USD', data.recipient);
      addMessage('assistant', confirmMsg);
      speakText(confirmMsg);
      return;
    }
    
    // Also check for transfer_details in response (alternative format)
    if (data.transfer_details && data.transfer_details.amount && data.transfer_details.recipient) {
      console.log('Setting pending transfer from transfer_details:', data.transfer_details);
      const transferDetails = {
        amount: parseFloat(data.transfer_details.amount),
        currency: data.transfer_details.currency || 'USD',
        recipient: data.transfer_details.recipient
      };
      
      setPendingTransfer(transferDetails);
      
      const confirmMsg = getConfirmationPrompt(transferDetails.amount, transferDetails.currency, transferDetails.recipient);
      addMessage('assistant', confirmMsg);
      speakText(confirmMsg);
      return;
    }
    
    // Default: show response text
    const responseText = data.message || data.response_text || getDefaultResponse();
    addMessage('assistant', responseText);
    speakText(responseText);
  };

  // Add message to conversation
  const addMessage = (role, text) => {
    setConversation(prev => [...prev, {
      role,
      text,
      timestamp: new Date()
    }]);
  };

  // Get default response based on language
  const getDefaultResponse = () => {
    const defaults = {
      en: "I'm here to help! You can ask me to send money, check your balance, or view your transaction history.",
      fr: "Je suis là pour vous aider! Vous pouvez me demander d'envoyer de l'argent, de vérifier votre solde ou de voir votre historique.",
      ar: "أنا هنا للمساعدة! يمكنك أن تطلب مني إرسال الأموال أو التحقق من رصيدك أو عرض سجل معاملاتك.",
      es: "¡Estoy aquí para ayudarte! Puedes pedirme que envíe dinero, verifique tu saldo o vea tu historial de transacciones.",
      pt: "Estou aqui para ajudar! Você pode me pedir para enviar dinheiro, verificar seu saldo ou ver seu histórico de transações.",
      de: "Ich bin hier, um zu helfen! Sie können mich bitten, Geld zu senden, Ihren Kontostand zu überprüfen oder Ihre Transaktionshistorie anzuzeigen.",
      it: "Sono qui per aiutarti! Puoi chiedermi di inviare denaro, controllare il tuo saldo o visualizzare la cronologia delle transazioni.",
      zh: "我在这里帮助您！您可以让我发送资金、查看余额或查看交易历史。",
      hi: "मैं मदद के लिए यहाँ हूँ! आप मुझसे पैसे भेजने, अपना बैलेंस जांचने या अपना लेनदेन इतिहास देखने के लिए कह सकते हैं।",
      ja: "お手伝いします！送金、残高確認、取引履歴の表示をお願いできます。",
      ko: "도움이 필요하시면 말씀해 주세요! 송금, 잔액 확인, 거래 내역 조회를 요청할 수 있습니다.",
      tr: "Yardım etmek için buradayım! Para göndermemi, bakiyenizi kontrol etmemi veya işlem geçmişinizi görüntülememi isteyebilirsiniz."
    };
    return defaults[selectedLanguage] || defaults.en;
  };

  // Get error message based on language
  const getErrorMessage = () => {
    const errors = {
      en: "Sorry, I didn't understand that. Please try again.",
      fr: "Désolé, je n'ai pas compris. Veuillez réessayer.",
      ar: "عذراً، لم أفهم ذلك. يرجى المحاولة مرة أخرى.",
      es: "Lo siento, no entendí eso. Por favor, inténtalo de nuevo.",
      pt: "Desculpe, não entendi. Por favor, tente novamente.",
      de: "Entschuldigung, das habe ich nicht verstanden. Bitte versuchen Sie es erneut.",
      it: "Scusa, non ho capito. Per favore riprova.",
      zh: "抱歉，我没有理解。请再试一次。",
      hi: "क्षमा करें, मुझे समझ नहीं आया। कृपया पुनः प्रयास करें।",
      ja: "申し訳ありません、理解できませんでした。もう一度お試しください。",
      ko: "죄송합니다, 이해하지 못했습니다. 다시 시도해 주세요.",
      tr: "Üzgünüm, anlamadım. Lütfen tekrar deneyin."
    };
    return errors[selectedLanguage] || errors.en;
  };

  // Handle mic button click
  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Handle text submit
  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (textInput.trim() && !isProcessing) {
      processTextCommand(textInput);
    }
  };

  // Handle quick command click
  const handleQuickCommand = (command) => {
    processTextCommand(command);
  };

  // Handle example click
  const handleExampleClick = (example) => {
    processTextCommand(example);
  };

  // Get status text
  const getStatusText = () => {
    switch (status) {
      case 'listening': return lang.listening;
      case 'processing': return lang.processing;
      default: return lang.tapToSpeak;
    }
  };

  // Get button color
  const getButtonColor = () => {
    if (isProcessing) return 'bg-gray-400';
    if (isRecording) return 'bg-red-500 hover:bg-red-600 animate-pulse';
    return 'bg-emerald-500 hover:bg-emerald-600';
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      {/* Language Selection Modal - Shows on first visit */}
      <Dialog open={showLanguageModal} onOpenChange={setShowLanguageModal}>
        <DialogContent className="sm:max-w-md" data-testid="language-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Languages className="w-6 h-6 text-emerald-500" />
              Select Your Language
            </DialogTitle>
            <DialogDescription>
              Choose a language for your voice assistant. You can change this later.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 py-4">
            {Object.entries(LANGUAGES).map(([code, language]) => (
              <button
                key={code}
                onClick={() => handleLanguageSelect(code)}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all group"
                data-testid={`modal-lang-${code}`}
              >
                <span className="text-3xl">{language.flag}</span>
                <span className="text-sm font-medium text-gray-700 group-hover:text-emerald-700">
                  {language.name}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-2xl mx-auto">
          
          {/* Header with Language Selection */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-6 h-6 text-emerald-500" />
              <h1 className="text-2xl font-bold text-gray-800">
                Voice Assistant
              </h1>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              {selectedLanguage === 'en' && "Speak or type to send money, check balance & more"}
              {selectedLanguage === 'fr' && "Parlez ou tapez pour envoyer de l'argent, vérifier le solde et plus"}
              {selectedLanguage === 'ar' && "تحدث أو اكتب لإرسال الأموال والتحقق من الرصيد والمزيد"}
              {selectedLanguage === 'es' && "Habla o escribe para enviar dinero, verificar saldo y más"}
            </p>

            {/* Language Selector - Grid Layout for All 12 Languages */}
            <div className="relative mb-4">
              <div className="flex flex-wrap justify-center gap-2 pb-2 px-1">
                {Object.entries(LANGUAGES).map(([code, language]) => (
                  <button
                    key={code}
                    onClick={() => {
                      setSelectedLanguage(code);
                      sessionStorage.setItem('voiceLanguage', code);
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                      selectedLanguage === code
                        ? 'bg-emerald-500 text-white shadow-lg scale-105'
                        : 'bg-white text-gray-700 border hover:border-emerald-300 hover:bg-emerald-50'
                    }`}
                    data-testid={`lang-${code}`}
                  >
                    <span className="mr-0.5">{language.flag}</span>
                    <span className="hidden sm:inline">{language.name}</span>
                    <span className="sm:hidden">{code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Conversation Area */}
          <Card className="mb-4 shadow-lg border-0">
            <CardContent className="p-4">
              <div 
                className="h-64 overflow-y-auto space-y-3 mb-4 scroll-smooth"
                dir={selectedLanguage === 'ar' ? 'rtl' : 'ltr'}
              >
                {conversation.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-emerald-500 text-white rounded-br-sm'
                          : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                      }`}
                    >
                      <p className="text-sm">{msg.text}</p>
                    </div>
                  </div>
                ))}
                {isProcessing && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 px-4 py-2 rounded-2xl rounded-bl-sm">
                      <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
                    </div>
                  </div>
                )}
                <div ref={conversationEndRef} />
              </div>

              {/* Text Input */}
              <form onSubmit={handleTextSubmit} className="flex gap-2">
                <Input
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder={lang.placeholder}
                  disabled={isProcessing || isRecording}
                  className="flex-1"
                  dir={selectedLanguage === 'ar' ? 'rtl' : 'ltr'}
                />
                <Button
                  type="submit"
                  disabled={isProcessing || isRecording || !textInput.trim()}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Created Transfer - Claim Code Display - SHOWN PROMINENTLY AT TOP */}
          {createdTransfer && (
            <Card ref={createdTransferRef} className="mb-4 shadow-xl border-4 border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-100" data-testid="created-transfer-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg">
                    <Sparkles className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-emerald-800">
                      {selectedLanguage === 'es' ? '✅ ¡Transferencia Creada!' :
                       selectedLanguage === 'fr' ? '✅ Transfert Créé!' :
                       selectedLanguage === 'ar' ? '✅ تم إنشاء التحويل!' :
                       '✅ Transfer Created Successfully!'}
                    </h3>
                    <p className="text-sm text-emerald-600 font-medium">
                      {createdTransfer.currency} {createdTransfer.amount} → {createdTransfer.recipient}
                    </p>
                  </div>
                </div>
                
                {/* Important Notice */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-red-700 text-sm font-semibold">
                    ⚠️ {selectedLanguage === 'es' ? '¡GUARDA ESTE CÓDIGO! No lo almacenamos.' :
                        selectedLanguage === 'fr' ? 'SAUVEGARDEZ CE CODE! Nous ne le stockons pas.' :
                        selectedLanguage === 'ar' ? 'احفظ هذا الرمز! نحن لا نخزنه.' :
                        'SAVE THIS CODE! We do not store it.'}
                  </p>
                </div>
                
                {/* Claim Code */}
                <div className="bg-white rounded-lg p-4 mb-4 shadow-inner">
                  <p className="text-sm text-gray-600 mb-2 font-medium">
                    {selectedLanguage === 'es' ? '🔑 Código de Reclamo (12 palabras):' :
                     selectedLanguage === 'fr' ? '🔑 Code de Réclamation (12 mots):' :
                     selectedLanguage === 'ar' ? '🔑 رمز المطالبة (12 كلمة):' :
                     '🔑 Claim Code (12 words):'}
                  </p>
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 font-mono text-base break-all text-amber-900">
                    {createdTransfer.mnemonic}
                  </div>
                </div>
                
                {/* Security Answer */}
                <div className="bg-white rounded-lg p-4 mb-4 shadow-inner">
                  <p className="text-sm text-gray-600 mb-1 font-medium">
                    {selectedLanguage === 'es' ? '🔒 Respuesta de Seguridad:' :
                     selectedLanguage === 'fr' ? '🔒 Réponse de Sécurité:' :
                     selectedLanguage === 'ar' ? '🔒 إجابة الأمان:' :
                     '🔒 Security Answer:'}
                  </p>
                  <p className="font-bold text-lg text-purple-600">
                    {createdTransfer.recipient?.split(' ').pop()}
                  </p>
                </div>
                
                {/* Wallet Address - For Guardarian payment */}
                <div className="bg-white rounded-lg p-4 mb-4 shadow-inner">
                  <p className="text-sm text-gray-600 mb-2 font-medium">
                    {selectedLanguage === 'es' ? '💳 Dirección de Billetera (para pago):' :
                     selectedLanguage === 'fr' ? '💳 Adresse du Portefeuille (pour paiement):' :
                     selectedLanguage === 'ar' ? '💳 عنوان المحفظة (للدفع):' :
                     '💳 Wallet Address (for payment):'}
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-gray-100 px-3 py-2 rounded flex-1 truncate font-mono border">
                      {createdTransfer.wallet_address}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(createdTransfer.wallet_address);
                        toast.success('Wallet address copied!');
                      }}
                      className="shrink-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {selectedLanguage === 'es' ? 'Usa esta dirección cuando Guardarian la solicite' :
                     selectedLanguage === 'fr' ? 'Utilisez cette adresse lorsque Guardarian la demande' :
                     selectedLanguage === 'ar' ? 'استخدم هذا العنوان عندما يطلبه Guardarian' :
                     'Use this address when Guardarian asks for it'}
                  </p>
                </div>
                
                {/* Actions */}
                <div className="flex gap-3">
                  <Button 
                    onClick={() => speakClaimCode(createdTransfer.mnemonic)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    size="lg"
                  >
                    <Volume2 className="w-5 h-5 mr-2" />
                    {selectedLanguage === 'es' ? 'Leer Código' :
                     selectedLanguage === 'fr' ? 'Lire le Code' :
                     selectedLanguage === 'ar' ? 'اقرأ الكود' :
                     'Read Code Aloud'}
                  </Button>
                  <Button 
                    onClick={() => {
                      navigator.clipboard.writeText(createdTransfer.mnemonic);
                      toast.success('Claim code copied!');
                    }}
                    variant="outline"
                    className="flex-1"
                    size="lg"
                  >
                    <Copy className="w-5 h-5 mr-2" />
                    {selectedLanguage === 'es' ? 'Copiar Código' :
                     selectedLanguage === 'fr' ? 'Copier le Code' :
                     selectedLanguage === 'ar' ? 'نسخ الكود' :
                     'Copy Code'}
                  </Button>
                </div>
                
                {/* Proceed to Payment Button */}
                <Button 
                  onClick={() => {
                    navigate('/send-money', { 
                      state: { 
                        prefill: {
                          amount: createdTransfer.amount,
                          currency: createdTransfer.currency,
                          recipient: createdTransfer.recipient,
                          walletAddress: createdTransfer.wallet_address,
                          skipToPayment: true
                        }
                      }
                    });
                  }}
                  className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
                  size="lg"
                >
                  {selectedLanguage === 'es' ? 'Proceder al Pago' :
                   selectedLanguage === 'fr' ? 'Procéder au Paiement' :
                   selectedLanguage === 'ar' ? 'المتابعة للدفع' :
                   'Proceed to Payment'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                {/* New Transfer Button */}
                <Button 
                  onClick={() => setCreatedTransfer(null)}
                  variant="ghost"
                  className="w-full mt-2"
                >
                  {selectedLanguage === 'es' ? 'Nueva Transferencia' :
                   selectedLanguage === 'fr' ? 'Nouveau Transfert' :
                   selectedLanguage === 'ar' ? 'تحويل جديد' :
                   'Start New Transfer'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Pending Transfer Preview */}
          {pendingTransfer && (
            <Card className="mb-4 shadow-lg border-2 border-amber-400 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center">
                    <Send className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800">
                      {selectedLanguage === 'es' ? 'Confirmar Transferencia' : 
                       selectedLanguage === 'fr' ? 'Confirmer le Transfert' :
                       selectedLanguage === 'ar' ? 'تأكيد التحويل' :
                       'Confirm Transfer'}
                    </h3>
                    <p className="text-xs text-amber-600">
                      {selectedLanguage === 'es' ? 'Di "confirmar" o "cancelar"' :
                       selectedLanguage === 'fr' ? 'Dites "confirmer" ou "annuler"' :
                       selectedLanguage === 'ar' ? 'قل "تأكيد" أو "إلغاء"' :
                       'Say "confirm" or "cancel"'}
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 mb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">
                      {selectedLanguage === 'es' ? 'Monto:' : 
                       selectedLanguage === 'fr' ? 'Montant:' :
                       selectedLanguage === 'ar' ? 'المبلغ:' :
                       'Amount:'}
                    </span>
                    <span className="font-bold text-xl text-emerald-600">
                      {pendingTransfer.currency} {pendingTransfer.amount}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600">
                      {selectedLanguage === 'es' ? 'Para:' :
                       selectedLanguage === 'fr' ? 'Pour:' :
                       selectedLanguage === 'ar' ? 'إلى:' :
                       'To:'}
                    </span>
                    <span className="font-medium">{pendingTransfer.recipient}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={createVoiceTransfer}
                    disabled={isCreatingTransfer}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {isCreatingTransfer ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    {selectedLanguage === 'es' ? 'Confirmar' :
                     selectedLanguage === 'fr' ? 'Confirmer' :
                     selectedLanguage === 'ar' ? 'تأكيد' :
                     'Confirm'}
                  </Button>
                  <Button 
                    onClick={cancelPendingTransfer}
                    variant="outline"
                    className="flex-1"
                    disabled={isCreatingTransfer}
                  >
                    {selectedLanguage === 'es' ? 'Cancelar' :
                     selectedLanguage === 'fr' ? 'Annuler' :
                     selectedLanguage === 'ar' ? 'إلغاء' :
                     'Cancel'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Voice Control */}
          <Card className="mb-4 shadow-lg border-0">
            <CardContent className="py-6 text-center">
              <div className="flex items-center justify-center gap-4">
                {/* Speaker button */}
                <button
                  onClick={isSpeaking ? stopSpeaking : () => {
                    const lastAssistant = [...conversation].reverse().find(m => m.role === 'assistant');
                    if (lastAssistant) speakText(lastAssistant.text);
                  }}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    isSpeaking 
                      ? 'bg-blue-500 text-white animate-pulse' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title={isSpeaking ? "Stop speaking" : "Replay last response"}
                >
                  <Volume2 className="w-5 h-5" />
                </button>

                {/* Mic Button */}
                <button
                  onClick={handleMicClick}
                  disabled={isProcessing}
                  className={`w-24 h-24 rounded-full ${getButtonColor()} text-white flex items-center justify-center shadow-lg transition-all transform hover:scale-105 active:scale-95`}
                >
                  {isProcessing ? (
                    <Loader2 className="w-12 h-12 animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="w-12 h-12" />
                  ) : (
                    <Mic className="w-12 h-12" />
                  )}
                </button>

                {/* Help button */}
                <button
                  onClick={() => handleQuickCommand(selectedLanguage === 'ar' ? 'مساعدة' : selectedLanguage === 'fr' ? 'Aide' : selectedLanguage === 'es' ? 'Ayuda' : 'Help')}
                  className="w-12 h-12 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center transition-all"
                  title="Help"
                >
                  <HelpCircle className="w-5 h-5" />
                </button>
              </div>
              
              <p className={`mt-4 text-sm font-medium ${isRecording ? 'text-red-600' : 'text-gray-600'}`}>
                {getStatusText()}
              </p>
            </CardContent>
          </Card>

          {/* Quick Commands */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-2 text-center font-medium">
              {selectedLanguage === 'en' && "Quick Commands"}
              {selectedLanguage === 'fr' && "Commandes Rapides"}
              {selectedLanguage === 'ar' && "أوامر سريعة"}
              {selectedLanguage === 'es' && "Comandos Rápidos"}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {lang.quickCommands.map((cmd, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickCommand(cmd)}
                  disabled={isProcessing}
                  className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:bg-emerald-50 hover:border-emerald-300 transition-all"
                >
                  {cmd}
                </button>
              ))}
            </div>
          </div>

          {/* Examples */}
          <Card className="shadow-lg border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                {selectedLanguage === 'en' && "Try saying..."}
                {selectedLanguage === 'fr' && "Essayez de dire..."}
                {selectedLanguage === 'ar' && "جرب قول..."}
                {selectedLanguage === 'es' && "Intenta decir..."}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {lang.examples.map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleExampleClick(example.text)}
                    disabled={isProcessing}
                    className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-emerald-50 rounded-lg text-left transition-all group"
                    dir={selectedLanguage === 'ar' ? 'rtl' : 'ltr'}
                  >
                    <span className="text-xl">{example.icon}</span>
                    <span className="text-sm text-gray-700 flex-1">{example.text}</span>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-500 transition-colors" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Language Info */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-400">
              <Globe className="w-3 h-3 inline mr-1" />
              Supports 12 languages: English, French, Arabic, Spanish, Portuguese, German, Italian, Chinese, Hindi, Japanese, Korean, Turkish
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}
